import { useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import type { IMedia } from "src/model/unityObject";
import {
  fetchMediasByIds,
  mediaByIdMapSelector,
  mediaByIdStatusMapSelector,
  type MediaByIdStatus,
} from "./state/gallerySlice";

export interface UseMediaByIdResult {
  media: IMedia | undefined;
  status: MediaByIdStatus;
}

export const useMediaById = (id?: number): UseMediaByIdResult => {
  const dispatch = useAppDispatch();
  const byId = useAppSelector(mediaByIdMapSelector);
  const byIdStatus = useAppSelector(mediaByIdStatusMapSelector);

  useEffect(() => {
    if (id === undefined) return;
    if (byIdStatus[id] !== undefined) return;
    void dispatch(fetchMediasByIds([id]));
  }, [dispatch, id, byIdStatus]);

  if (id === undefined) {
    return { media: undefined, status: "missing" };
  }

  return { media: byId[id], status: byIdStatus[id] ?? "loading" };
};

export const useMediasByIds = (ids: number[]): Record<number, UseMediaByIdResult> => {
  const dispatch = useAppDispatch();
  const byId = useAppSelector(mediaByIdMapSelector);
  const byIdStatus = useAppSelector(mediaByIdStatusMapSelector);
  const key = ids.join(",");

  useEffect(() => {
    const unresolvedIds = ids.filter((id) => byIdStatus[id] === undefined);
    if (unresolvedIds.length === 0) return;
    void dispatch(fetchMediasByIds(unresolvedIds));
  }, [dispatch, key, byIdStatus]);

  return useMemo(() => {
    const out: Record<number, UseMediaByIdResult> = {};
    for (const id of ids) {
      out[id] = { media: byId[id], status: byIdStatus[id] ?? "loading" };
    }
    return out;
  }, [byId, byIdStatus, key]);
};
